import React, { useState } from 'react';
import { Box, ButtonBase, Card, Grid, Typography } from '@mui/material';
import { marketingCardSx, marketingPalette } from './marketingSystem';
import ConductorHeroMark from './graphics/ConductorHeroMark';

type ScenarioPreset = {
  id: string;
  label: string;
  kicker: string;
  scenarioName: string;
  orderTotal: string;
  customerContext: string;
  deviceContext: string;
  businessObjective: string;
  recommendedMethods: string[];
  checkoutHeadline: string;
  checkoutChange: string;
  cardRoute: string;
  routeSummary: string;
  rationale: string;
  tradeoff: string;
  derivedMacroTitle: string;
  derivedMacroDetail: string;
  expectedImpact: {
    approval: string;
    cost: string;
    risk: string;
  };
  requestPayload: string;
  responsePayload: string;
};

const shellSx = {
  ...marketingCardSx,
  p: { xs: 1.15, md: 1.35 },
  borderRadius: 1.25,
  borderColor: 'rgba(173,188,209,0.72)',
  background: 'linear-gradient(180deg, rgba(251,252,254,0.995) 0%, rgba(244,247,251,0.995) 100%)',
  boxShadow: '0 22px 60px rgba(15,23,42,0.075)',
};

const darkBoardSx = {
  borderRadius: 1,
  border: '1px solid rgba(105,130,168,0.28)',
  background: 'linear-gradient(180deg, #102349 0%, #0d1d3b 100%)',
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
};

const lightBoardSx = {
  borderRadius: 1,
  border: '1px solid rgba(214,223,234,0.9)',
  backgroundColor: 'rgba(255,255,255,0.99)',
};

const overlineSx = {
  fontSize: '0.64rem',
  fontWeight: 400,
  letterSpacing: '0.11em',
  textTransform: 'uppercase' as const,
};

const darkOverlineSx = {
  ...overlineSx,
  color: 'rgba(191,219,254,0.72)',
};

const lightOverlineSx = {
  ...overlineSx,
  color: marketingPalette.muted,
};

const monoSx = {
  fontFamily:
    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
  fontSize: '0.74rem',
  lineHeight: 1.68,
  color: '#dbeafe',
  whiteSpace: 'pre-wrap' as const,
  wordBreak: 'break-word' as const,
};

export const ecommerceScenarioPresets: ScenarioPreset[] = [
  {
    id: 'domestic-mobile',
    label: 'Domestic low-risk',
    kicker: 'US mobile checkout',
    scenarioName: 'Repeat buyer on iPhone',
    orderTotal: '$146',
    customerContext: 'US shopper · repeat buyer',
    deviceContext: 'iPhone · mobile web',
    businessObjective: 'Push harder on approval without giving back margin',
    recommendedMethods: ['Apple Pay', 'Card', 'Open Banking'],
    checkoutHeadline: 'Wallet-first mobile checkout',
    checkoutChange: 'Apple Pay moves to the lead because the returning mobile buyer has the highest modeled success with lower friction.',
    cardRoute: 'Primary authorization path',
    routeSummary: 'Card stays available as the fallback, positioned behind the stronger wallet-led flow.',
    rationale: 'Wallet-first removes friction on the strongest segment while Card stays available as the fallback path.',
    tradeoff: 'Approval lifts in checkout while fee discipline still stays in line.',
    derivedMacroTitle: 'Fee structure stays healthy',
    derivedMacroDetail: 'The stronger checkout path does not push the business into a worse processor cost position.',
    expectedImpact: {
      approval: '+11 pts',
      cost: '-18 bps',
      risk: 'Held',
    },
    requestPayload: `{
  "context": "mobile_checkout_us",
  "priority": { "conversion": 45, "cost": 25, "risk": 30 },
  "merchant_id": "merchant_us_001",
  "basket_total": 14600
}`,
    responsePayload: `{
  "recommended_methods": ["apple_pay", "card", "open_banking"],
  "method_emphasis": { "lead": "apple_pay", "fallback": "card" },
  "expected_impact": { "approval_pts": 11, "fee_bps": -18, "risk": "held" }
}`,
  },
  {
    id: 'cross-border',
    label: 'International checkout',
    kicker: 'Cross-border mobile',
    scenarioName: 'New UK buyer in USD flow',
    orderTotal: '$184',
    customerContext: 'UK shopper · new buyer',
    deviceContext: 'Safari · mobile web',
    businessObjective: 'Recover approval without widening FX and route drag',
    recommendedMethods: ['PayPal', 'Card', 'Apple Pay'],
    checkoutHeadline: 'Cross-border checkout with local-fit lead',
    checkoutChange: 'The lead method shifts because the cross-border buyer converts better with a familiar wallet before Card is presented.',
    cardRoute: 'Local-authorization path',
    routeSummary: 'Card stays live, but it is no longer the lead — the cross-border wallet takes the front of the stack.',
    rationale: 'Cross-border fit matters more than keeping the domestic method order unchanged.',
    tradeoff: 'Approval improves without letting cross-border cost drift too far.',
    derivedMacroTitle: 'Cross-border cost stays in line',
    derivedMacroDetail: 'The route change keeps the approval win from turning into extra cross-border cost.',
    expectedImpact: {
      approval: '+7 pts',
      cost: '-6 bps',
      risk: 'Stable',
    },
    requestPayload: `{
  "context": "cross_border_mobile_checkout",
  "priority": { "conversion": 50, "cost": 20, "risk": 30 },
  "merchant_id": "merchant_uk_014",
  "basket_total": 18400
}`,
    responsePayload: `{
  "recommended_methods": ["paypal", "card", "apple_pay"],
  "method_emphasis": { "lead": "paypal", "fallback": "card" },
  "expected_impact": { "approval_pts": 7, "fee_bps": -6, "risk": "stable" }
}`,
  },
  {
    id: 'dispute-watch',
    label: 'Dispute-sensitive',
    kicker: 'Higher-risk ticket',
    scenarioName: 'Promo-driven returning buyer',
    orderTotal: '$228',
    customerContext: 'US shopper · dispute watch',
    deviceContext: 'Android · app checkout',
    businessObjective: 'Keep approval healthy while tightening risk control',
    recommendedMethods: ['Card', 'Apple Pay'],
    checkoutHeadline: 'Risk-managed checkout for a hotter segment',
    checkoutChange: 'The visible method mix tightens because the scenario has more dispute sensitivity than the low-risk baseline.',
    cardRoute: 'Risk-adjusted authorization path',
    routeSummary: 'Card stays visible, but the method mix tightens around a safer liability posture.',
    rationale: 'The answer still keeps conversion healthier, but the route and surrounding methods change because dispute pressure is higher.',
    tradeoff: 'The answer gives up some local lift so dispute pressure does not widen.',
    derivedMacroTitle: 'Chargeback risk stays tighter',
    derivedMacroDetail: 'The route and method discipline keep the business from winning locally while quietly taking on more dispute pressure.',
    expectedImpact: {
      approval: '+4 pts',
      cost: '+3 bps',
      risk: '-24 bps',
    },
    requestPayload: `{
  "context": "promo_checkout_risk_watch",
  "priority": { "conversion": 35, "cost": 20, "risk": 45 },
  "merchant_id": "merchant_us_001",
  "basket_total": 22800
}`,
    responsePayload: `{
  "recommended_methods": ["card", "apple_pay"],
  "method_emphasis": { "lead": "card", "risk_posture": "tightened" },
  "expected_impact": { "approval_pts": 4, "fee_bps": 3, "chargeback_bps": -24 }
}`,
  },
  {
    id: 'higher-ticket',
    label: 'Higher-ticket',
    kicker: 'Financing-sensitive checkout',
    scenarioName: 'Desktop financing consideration',
    orderTotal: '$612',
    customerContext: 'US shopper · high AOV',
    deviceContext: 'Desktop · browser checkout',
    businessObjective: 'Protect conversion on higher-ticket checkout without losing fee discipline',
    recommendedMethods: ['Affirm', 'Card', 'Apple Pay'],
    checkoutHeadline: 'Financing-first setup for higher-ticket demand',
    checkoutChange: 'Installments surface because financing meaningfully changes completion probability on the higher-ticket scenario.',
    cardRoute: 'Primary authorization path',
    routeSummary: 'Card remains available, but it is no longer the lead for this basket profile.',
    rationale: 'The answer changes because basket size changes the best visible setup even with the same merchant and stack.',
    tradeoff: 'Completion improves while the overall payment economics stay in line.',
    derivedMacroTitle: 'Margin stays in line',
    derivedMacroDetail: 'Installments appear where they help completion without letting the economics drift.',
    expectedImpact: {
      approval: '+9 pts',
      cost: '-4 bps',
      risk: 'Held',
    },
    requestPayload: `{
  "context": "desktop_high_aov_checkout",
  "priority": { "conversion": 50, "cost": 30, "risk": 20 },
  "merchant_id": "merchant_us_001",
  "basket_total": 61200
}`,
    responsePayload: `{
  "recommended_methods": ["affirm", "card", "apple_pay"],
  "method_emphasis": { "lead": "affirm", "fallback": "card" },
  "expected_impact": { "approval_pts": 9, "fee_bps": -4, "risk": "held" }
}`,
  },
];

const programFindings = [
  {
    label: 'Fee structure',
    detail: 'If a route helps approval but pushes the business toward a worse cost position, Ormal can hold that back.',
  },
  {
    label: 'Commitments',
    detail: 'If processor commitments are in play, Ormal can keep the wider economics in line instead of chasing a narrow local win.',
  },
  {
    label: 'Chargebacks',
    detail: 'If dispute exposure is rising, Ormal can tighten the answer even when the highest-converting route looks tempting.',
  },
];

const proofPilots = [
  {
    label: 'Pilot one',
    company: 'Luxury goods resale',
    headline: 'Checkout conversion moved from 57% to 72%',
    baseline: '57% checkout conversion',
    optimized: '72% checkout conversion',
    protected: 'Risk discipline stayed intact on high-value transactions',
  },
  {
    label: 'Pilot two',
    company: 'Marketplace multinational',
    headline: 'Chargebacks fell below 0.5% and blended payment cost fell 8%',
    baseline: 'Elevated chargebacks and higher blended cost',
    optimized: '< 0.5% chargebacks in key markets and -8% blended payment cost',
    protected: 'Checkout coverage stayed broad across key markets',
  },
];

const scenarioChipSx = (active: boolean) => ({
  px: 1.05,
  py: 0.72,
  borderRadius: 0.9,
  border: '1px solid',
  borderColor: active ? 'rgba(15,111,255,0.36)' : 'rgba(196,212,232,0.92)',
  backgroundColor: active ? 'rgba(15,111,255,0.08)' : '#fff',
  color: active ? marketingPalette.accent : marketingPalette.headline,
  textAlign: 'left' as const,
  transition: 'all 180ms ease',
});

const checkoutMethodSx = (active: boolean) => ({
  px: 1,
  py: 0.85,
  border: '1px solid',
  borderColor: active ? 'rgba(15,111,255,0.34)' : 'rgba(219,228,239,0.9)',
  borderRadius: 0.9,
  backgroundColor: active ? 'rgba(15,111,255,0.06)' : '#fff',
  display: 'grid',
  gap: 0.2,
  textAlign: 'left' as const,
});

const impactCellSx = {
  px: 0.9,
  py: 0.75,
  border: '1px solid rgba(120,143,180,0.2)',
  borderRadius: 0.8,
  backgroundColor: 'rgba(255,255,255,0.03)',
};

function useScenarioPreset(initialId?: string) {
  const initialIndex = Math.max(
    0,
    ecommerceScenarioPresets.findIndex((item) => item.id === initialId),
  );
  const [selectedIndex, setSelectedIndex] = useState(initialIndex === -1 ? 0 : initialIndex);
  const preset = ecommerceScenarioPresets[selectedIndex] ?? ecommerceScenarioPresets[0];

  return {
    preset,
    selectedIndex,
    setSelectedIndex,
  };
}

type ScenarioCheckoutHeroProps = {
  initialScenarioId?: string;
  showAdaptationNote?: boolean;
};

const scenarioSwitcherGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, minmax(0, 1fr))' },
  gap: 0.55,
};

const signalCellSx = {
  px: 0.9,
  py: 0.72,
  border: '1px solid rgba(120,143,180,0.2)',
  backgroundColor: 'rgba(255,255,255,0.03)',
};

export const LandingCheckoutHero: React.FC<ScenarioCheckoutHeroProps> = ({
  initialScenarioId,
  showAdaptationNote = false,
}) => {
  const { preset, selectedIndex, setSelectedIndex } = useScenarioPreset(initialScenarioId);

  return (
    <Card sx={shellSx}>
      <Box sx={{ display: 'grid', gap: 0.85 }}>
        <Box sx={scenarioSwitcherGridSx}>
          {ecommerceScenarioPresets.map((item, index) => (
            <ButtonBase key={item.id} onClick={() => setSelectedIndex(index)} sx={scenarioChipSx(index === selectedIndex)}>
              <Box>
                <Typography sx={{ ...lightOverlineSx, mb: 0.15 }}>{item.kicker}</Typography>
                <Typography sx={{ fontWeight: 400, fontSize: '0.82rem', lineHeight: 1.3 }}>{item.label}</Typography>
              </Box>
            </ButtonBase>
          ))}
        </Box>

        <Grid container spacing={1} alignItems="stretch">
          <Grid item xs={12} lg={7.35}>
            <Box sx={{ ...lightBoardSx, p: 1.05, minHeight: 428, height: '100%', display: 'grid', gridTemplateRows: 'auto auto 1fr auto', gap: 0.95 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'start' }}>
                <Box>
                  <Typography sx={{ ...lightOverlineSx, mb: 0.2 }}>Checkout recommendation</Typography>
                  <Typography sx={{ color: marketingPalette.headline, fontWeight: 400, fontSize: '1rem', lineHeight: 1.3 }}>
                    {preset.checkoutHeadline}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={{ ...lightOverlineSx, mb: 0.15 }}>Order total</Typography>
                  <Typography sx={{ color: marketingPalette.headline, fontWeight: 400 }}>{preset.orderTotal}</Typography>
                </Box>
              </Box>

              <Typography sx={{ color: marketingPalette.body, fontWeight: 400, fontSize: '0.8rem', lineHeight: 1.5, pb: 0.9, borderBottom: '1px solid rgba(219,228,239,0.9)' }}>
                {preset.scenarioName} · {preset.customerContext} · {preset.deviceContext}
              </Typography>

              <Box sx={{ display: 'grid', alignContent: 'start' }}>
                <Typography sx={{ ...lightOverlineSx, mb: 0.55 }}>What the buyer sees</Typography>
                <Box sx={{ display: 'grid', gap: 0.55 }}>
                  {preset.recommendedMethods.map((method, index) => (
                    <Box key={method} sx={checkoutMethodSx(index === 0)}>
                      <Typography sx={{ color: marketingPalette.headline, fontWeight: 400, fontSize: '0.88rem' }}>{method}</Typography>
                      <Typography sx={{ color: marketingPalette.body, fontSize: '0.76rem', lineHeight: 1.4 }}>
                        {index === 0 ? 'Shown first in checkout' : 'Stays available in checkout'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box sx={{ borderTop: '1px solid rgba(219,228,239,0.9)', pt: 0.85 }}>
                <Typography sx={lightOverlineSx}>Why checkout changed</Typography>
                <Typography sx={{ mt: 0.18, color: marketingPalette.headline, fontWeight: 400, fontSize: '0.84rem', lineHeight: 1.48 }}>
                  {preset.checkoutChange}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} lg={4.65}>
            <Box sx={{ ...darkBoardSx, p: 1.02, minHeight: 428, height: '100%', display: 'grid', gridTemplateRows: 'auto auto auto 1fr auto', gap: 0.85 }}>
              <Box>
                <Typography sx={{ ...darkOverlineSx, mb: 0.18 }}>Why this is the answer</Typography>
                <Typography sx={{ color: 'white', fontWeight: 400, fontSize: '1rem', lineHeight: 1.32 }}>
                  {preset.businessObjective}
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gap: 0.55 }}>
                <Box sx={signalCellSx}>
                  <Typography sx={darkOverlineSx}>Card route</Typography>
                  <Typography sx={{ mt: 0.18, color: 'white', fontWeight: 400, fontSize: '0.9rem' }}>{preset.cardRoute}</Typography>
                  <Typography sx={{ mt: 0.16, color: 'rgba(226,232,240,0.76)', fontSize: '0.78rem', lineHeight: 1.46 }}>
                    {preset.routeSummary}
                  </Typography>
                </Box>
                <Box sx={signalCellSx}>
                  <Typography sx={darkOverlineSx}>Commercial reason</Typography>
                  <Typography sx={{ mt: 0.18, color: 'white', fontWeight: 400, fontSize: '0.84rem', lineHeight: 1.46 }}>
                    {preset.rationale}
                  </Typography>
                </Box>
                <Box sx={signalCellSx}>
                  <Typography sx={darkOverlineSx}>What still stays in line</Typography>
                  <Typography sx={{ mt: 0.18, color: 'rgba(226,232,240,0.82)', fontSize: '0.8rem', lineHeight: 1.48 }}>
                    {preset.tradeoff}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 0.55 }}>
                {[
                  ['Approval', preset.expectedImpact.approval],
                  ['Fee drag', preset.expectedImpact.cost],
                  ['Risk', preset.expectedImpact.risk],
                ].map(([label, value]) => (
                  <Box key={label} sx={signalCellSx}>
                    <Typography sx={darkOverlineSx}>{label}</Typography>
                    <Typography sx={{ mt: 0.18, color: 'white', fontWeight: 400, fontSize: '0.9rem' }}>{value}</Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={{ px: 0.95, py: 0.86, borderTop: '1px solid rgba(120,143,180,0.18)', backgroundColor: 'rgba(15,111,255,0.08)' }}>
                <Typography sx={darkOverlineSx}>{preset.derivedMacroTitle}</Typography>
                <Typography sx={{ mt: 0.2, color: 'white', fontWeight: 400, fontSize: '0.84rem', lineHeight: 1.48 }}>
                  {preset.derivedMacroDetail}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {showAdaptationNote ? (
          <Box sx={{ ...darkBoardSx, p: 0.9 }}>
            <Typography sx={darkOverlineSx}>Why the answer changes</Typography>
            <Typography sx={{ mt: 0.18, color: 'rgba(226,232,240,0.82)', fontSize: '0.82rem', lineHeight: 1.52 }}>
              The visible checkout, hidden route decision, and expected impact all move when customer context, ticket size, cross-border fit, or dispute sensitivity change.
            </Typography>
          </Box>
        ) : null}
      </Box>
    </Card>
  );
};

export const HomepageDecisionHero: React.FC<{ initialScenarioId?: string }> = ({ initialScenarioId }) => {
  const { preset } = useScenarioPreset(initialScenarioId);

  return (
    <Card sx={shellSx}>
      <Grid container spacing={1} alignItems="stretch">
        <Grid item xs={12} lg={7.35}>
          <Box
            sx={{
              ...lightBoardSx,
              p: 1.2,
              minHeight: 404,
              height: '100%',
              display: 'grid',
              gridTemplateRows: 'auto auto 1fr auto',
              gap: 0.95,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'start' }}>
              <Box>
                <Typography sx={{ ...lightOverlineSx, mb: 0.18 }}>Recommended checkout</Typography>
                <Typography sx={{ color: marketingPalette.headline, fontWeight: 400, fontSize: '1.08rem', lineHeight: 1.28 }}>
                  {preset.checkoutHeadline}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ ...lightOverlineSx, mb: 0.14 }}>Scenario</Typography>
                <Typography sx={{ color: marketingPalette.headline, fontWeight: 400, fontSize: '0.82rem', lineHeight: 1.35 }}>
                  {preset.label}
                </Typography>
              </Box>
            </Box>

            <Typography
              sx={{
                color: marketingPalette.body,
                fontWeight: 400,
                fontSize: '0.79rem',
                lineHeight: 1.5,
                pb: 0.86,
                borderBottom: '1px solid rgba(219,228,239,0.9)',
              }}
            >
              {preset.customerContext} · {preset.deviceContext} · {preset.orderTotal}
            </Typography>

            <Box sx={{ display: 'grid', gap: 0.72, alignContent: 'start' }}>
              <Box
                sx={{
                  border: '1px solid rgba(15,111,255,0.24)',
                  background: 'linear-gradient(180deg, rgba(15,111,255,0.06) 0%, rgba(255,255,255,0.98) 100%)',
                  px: 1,
                  py: 0.92,
                }}
              >
                <Typography sx={lightOverlineSx}>Lead method</Typography>
                <Typography sx={{ mt: 0.14, color: marketingPalette.headline, fontWeight: 400, fontSize: '1rem' }}>
                  {preset.recommendedMethods[0]}
                </Typography>
                <Typography sx={{ mt: 0.18, color: marketingPalette.body, fontSize: '0.78rem', lineHeight: 1.45 }}>
                  Shown first because it gives this buyer the cleanest path to completion.
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.55 }}>
                {preset.recommendedMethods.slice(1).map((method) => (
                  <Box
                    key={method}
                    sx={{
                      px: 0.9,
                      py: 0.68,
                      border: '1px solid rgba(219,228,239,0.9)',
                      backgroundColor: '#fff',
                    }}
                  >
                    <Typography sx={{ color: marketingPalette.headline, fontWeight: 400, fontSize: '0.8rem' }}>{method}</Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={{ borderTop: '1px solid rgba(219,228,239,0.9)', pt: 0.82 }}>
                <Typography sx={lightOverlineSx}>Why the visible checkout changes</Typography>
                <Typography sx={{ mt: 0.18, color: marketingPalette.headline, fontWeight: 400, fontSize: '0.84rem', lineHeight: 1.52 }}>
                  {preset.checkoutChange}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 0.6,
                borderTop: '1px solid rgba(219,228,239,0.9)',
                pt: 0.82,
              }}
            >
              <Box>
                <Typography sx={lightOverlineSx}>Card handling</Typography>
                <Typography sx={{ mt: 0.18, color: marketingPalette.headline, fontWeight: 400, fontSize: '0.88rem' }}>
                  {preset.cardRoute}
                </Typography>
              </Box>
              <Box>
                <Typography sx={lightOverlineSx}>What stays healthy</Typography>
                <Typography sx={{ mt: 0.18, color: marketingPalette.body, fontWeight: 400, fontSize: '0.8rem', lineHeight: 1.45 }}>
                  {preset.derivedMacroTitle}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} lg={4.65}>
          <Box
            sx={{
              ...darkBoardSx,
              p: 1.08,
              minHeight: 404,
              height: '100%',
              display: 'grid',
              gridTemplateRows: 'auto auto auto 1fr',
              gap: 0.9,
            }}
          >
            <Box>
              <Typography sx={{ ...darkOverlineSx, mb: 0.18 }}>Decision outcome</Typography>
              <Typography sx={{ color: 'white', fontWeight: 400, fontSize: '1rem', lineHeight: 1.34 }}>
                {preset.businessObjective}
              </Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 0.55 }}>
              {[
                ['Approval', preset.expectedImpact.approval],
                ['Fee drag', preset.expectedImpact.cost],
                ['Risk', preset.expectedImpact.risk],
              ].map(([label, value]) => (
                <Box key={label} sx={signalCellSx}>
                  <Typography sx={darkOverlineSx}>{label}</Typography>
                  <Typography sx={{ mt: 0.18, color: 'white', fontWeight: 400, fontSize: '0.92rem' }}>{value}</Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ px: 0.95, py: 0.84, border: '1px solid rgba(120,143,180,0.2)', backgroundColor: 'rgba(255,255,255,0.03)' }}>
              <Typography sx={darkOverlineSx}>Authorization posture</Typography>
              <Typography sx={{ mt: 0.18, color: 'white', fontWeight: 400, fontSize: '0.92rem' }}>{preset.cardRoute}</Typography>
              <Typography sx={{ mt: 0.16, color: 'rgba(226,232,240,0.82)', fontSize: '0.79rem', lineHeight: 1.46 }}>
                {preset.routeSummary}
              </Typography>
            </Box>

            <Box sx={{ display: 'grid', gap: 0.58, alignContent: 'start' }}>
              <Box sx={{ borderBottom: '1px solid rgba(120,143,180,0.18)', pb: 0.72 }}>
                <Typography sx={darkOverlineSx}>Why this wins</Typography>
                <Typography sx={{ mt: 0.18, color: 'white', fontWeight: 400, fontSize: '0.84rem', lineHeight: 1.5 }}>
                  {preset.rationale}
                </Typography>
              </Box>
              <Box sx={{ pt: 0.08 }}>
                <Typography sx={darkOverlineSx}>Still healthy</Typography>
                <Typography sx={{ mt: 0.18, color: 'white', fontWeight: 400, fontSize: '0.84rem', lineHeight: 1.5 }}>
                  {preset.derivedMacroDetail}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Card>
  );
};

export const LandingSignalCue: React.FC<{ initialScenarioId?: string }> = ({ initialScenarioId }) => {
  const { preset } = useScenarioPreset(initialScenarioId);

  return (
    <Card sx={{ ...shellSx, p: 1 }}>
      <Box sx={{ display: 'grid', gap: 0.8 }}>
        <Box
          sx={{
            ...lightBoardSx,
            p: 1,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1.15fr 0.9fr 0.95fr' },
            gap: 0.7,
            alignItems: 'stretch',
          }}
        >
          <Box sx={{ display: 'grid', gap: 0.28 }}>
            <Typography sx={lightOverlineSx}>Visible checkout</Typography>
            <Typography sx={{ color: marketingPalette.headline, fontWeight: 400, fontSize: '0.98rem', lineHeight: 1.3 }}>
              {preset.recommendedMethods[0]} leads for this buyer
            </Typography>
            <Typography sx={{ color: marketingPalette.body, fontSize: '0.8rem', lineHeight: 1.5 }}>
              {preset.checkoutChange}
            </Typography>
          </Box>

          <Box
            sx={{
              borderLeft: { sm: '1px solid rgba(219,228,239,0.9)' },
              pl: { sm: 0.8 },
              display: 'grid',
              alignContent: 'start',
              gap: 0.28,
            }}
          >
            <Typography sx={lightOverlineSx}>Behind Card</Typography>
            <Typography sx={{ color: marketingPalette.headline, fontWeight: 400, fontSize: '0.9rem', lineHeight: 1.35 }}>
              {preset.cardRoute}
            </Typography>
            <Typography sx={{ color: marketingPalette.body, fontSize: '0.79rem', lineHeight: 1.48 }}>
              {preset.routeSummary}
            </Typography>
          </Box>

          <Box
            sx={{
              borderLeft: { sm: '1px solid rgba(219,228,239,0.9)' },
              pl: { sm: 0.8 },
              display: 'grid',
              gap: 0.45,
            }}
          >
            <Typography sx={lightOverlineSx}>Business effect</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 0.35 }}>
              {[
                ['Approval', preset.expectedImpact.approval],
                ['Fees', preset.expectedImpact.cost],
                ['Risk', preset.expectedImpact.risk],
              ].map(([label, value]) => (
                <Box
                  key={label}
                  sx={{
                    px: 0.5,
                    py: 0.48,
                    border: '1px solid rgba(219,228,239,0.9)',
                    backgroundColor: '#fff',
                  }}
                >
                  <Typography sx={{ ...lightOverlineSx, fontSize: '0.58rem', letterSpacing: '0.08em' }}>{label}</Typography>
                  <Typography sx={{ mt: 0.14, color: marketingPalette.headline, fontWeight: 400, fontSize: '0.78rem', lineHeight: 1.25 }}>
                    {value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Box sx={{ ...darkBoardSx, px: 0.95, py: 0.82 }}>
          <Typography sx={darkOverlineSx}>Why this matters</Typography>
          <Typography sx={{ mt: 0.18, color: 'white', fontWeight: 400, fontSize: '0.84rem', lineHeight: 1.5 }}>
            Ormal changes the visible checkout and the route behind Card together, while keeping {preset.derivedMacroTitle.toLowerCase()}.
          </Typography>
        </Box>
      </Box>
    </Card>
  );
};

export const LandingBrandGraphic: React.FC = () => {
  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: { xs: 320, md: 390 },
        border: '1px solid rgba(211,223,239,0.78)',
        background:
          'radial-gradient(circle at 50% 46%, rgba(96,165,250,0.28), rgba(255,255,255,0) 16%), radial-gradient(circle at 18% 20%, rgba(191,219,254,0.2), rgba(255,255,255,0) 28%), radial-gradient(circle at 86% 18%, rgba(191,219,254,0.28), rgba(255,255,255,0) 24%), linear-gradient(180deg, rgba(255,255,255,0.995) 0%, rgba(245,249,255,0.995) 100%)',
        boxShadow: '0 36px 92px rgba(15,23,42,0.09)',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 16,
          border: '1px solid rgba(225,233,243,0.84)',
          background:
            'linear-gradient(180deg, rgba(255,255,255,0.68) 0%, rgba(247,250,255,0.74) 100%)',
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          inset: '0',
          background:
            'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(191,219,254,0.16) 34%, rgba(191,219,254,0.16) 66%, rgba(255,255,255,0) 100%)',
          maskImage:
            'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.9) 18%, rgba(0,0,0,0.9) 82%, rgba(0,0,0,0) 100%)',
          opacity: 0.5,
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          inset: { xs: '18% 22%', md: '16% 24%' },
          border: '1px solid rgba(191,219,254,0.72)',
          borderRadius: '50%',
          opacity: 0.6,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: { xs: '28% 32%', md: '26% 34%' },
          border: '1px solid rgba(191,219,254,0.46)',
          borderRadius: '50%',
          opacity: 0.54,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: { xs: '38% 42%', md: '36% 44%' },
          border: '1px solid rgba(191,219,254,0.34)',
          borderRadius: '50%',
          opacity: 0.48,
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          display: 'grid',
          justifyItems: 'center',
          gap: 0.85,
        }}
      >
        <Box
          sx={{
            width: { xs: 86, md: 108 },
            height: { xs: 86, md: 108 },
            display: 'grid',
            placeItems: 'center',
            background:
              'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.98), rgba(219,234,254,0.92) 58%, rgba(96,165,250,0.32) 100%)',
            boxShadow: '0 0 0 22px rgba(96,165,250,0.06), 0 0 86px rgba(96,165,250,0.2)',
          }}
        >
          <ConductorHeroMark />
        </Box>
      </Box>

      <Box
        sx={{
          position: 'absolute',
          left: { xs: 28, md: 36 },
          right: { xs: 28, md: 36 },
          top: '50%',
          height: 1,
          background:
            'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(147,197,253,0.42) 18%, rgba(147,197,253,0.42) 82%, rgba(255,255,255,0) 100%)',
          opacity: 0.74,
        }}
      />

      {[
        { label: 'Approval', position: { top: { xs: 38, md: 48 }, left: { xs: 28, md: 38 } } },
        { label: 'Margin', position: { top: '50%', right: { xs: 24, md: 34 }, transform: 'translateY(-50%)' } },
        { label: 'Control', position: { bottom: { xs: 34, md: 42 }, left: { xs: 34, md: 44 } } },
      ].map((item) => (
        <Typography
          key={item.label}
          sx={{
            position: 'absolute',
            ...item.position,
            color: marketingPalette.accent,
            fontSize: '0.66rem',
            fontWeight: 400,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
          }}
        >
          {item.label}
        </Typography>
      ))}
    </Box>
  );
};

export const ScenarioCheckoutHero: React.FC<ScenarioCheckoutHeroProps> = (props) => <LandingCheckoutHero {...props} />;

export const SolutionScenarioBoard: React.FC<ScenarioCheckoutHeroProps> = ({
  initialScenarioId,
  showAdaptationNote = true,
}) => {
  useScenarioPreset(initialScenarioId);

  return (
    <Card sx={shellSx}>
      <Box sx={{ display: 'grid', gap: 0.85 }}>
        <Grid container spacing={1} alignItems="stretch">
          <Grid item xs={12} lg={6.2}>
            <Box sx={{ ...lightBoardSx, p: 1.02, minHeight: 414, height: '100%', display: 'grid', gap: 0.82, alignContent: 'start' }}>
              <Box>
                <Typography sx={lightOverlineSx}>What moves at the payment moment</Typography>
                <Typography sx={{ mt: 0.18, color: marketingPalette.headline, fontWeight: 400, fontSize: '1rem', lineHeight: 1.3 }}>
                  Ormal optimizes the visible checkout and the route underneath it as one decision.
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gap: 0.58 }}>
                {[
                  ['Visible checkout', 'Method order, lead method, and fallback presentation move together.'],
                  ['Authorization posture', 'Routing can change without forcing the buyer to see a different Card surface.'],
                  ['Business priority', 'The recommendation is aligned to conversion, economics, or chargeback control based on what matters most.'],
                ].map(([label, body]) => (
                  <Box key={label} sx={{ px: 0.9, py: 0.8, border: '1px solid rgba(219,228,239,0.9)', backgroundColor: '#fff' }}>
                    <Typography sx={lightOverlineSx}>{label}</Typography>
                    <Typography sx={{ mt: 0.18, color: marketingPalette.headline, fontWeight: 400, fontSize: '0.82rem', lineHeight: 1.5 }}>
                      {body}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={{ borderTop: '1px solid rgba(219,228,239,0.9)', pt: 0.78 }}>
                <Typography sx={lightOverlineSx}>What that means in practice</Typography>
                <Typography sx={{ mt: 0.16, color: marketingPalette.body, fontWeight: 400, fontSize: '0.82rem', lineHeight: 1.52 }}>
                  The answer is not one isolated routing choice or one isolated checkout tweak. It is one returned recommendation for the full payment moment.
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} lg={5.8}>
            <Box sx={{ ...darkBoardSx, p: 1.02, minHeight: 414, height: '100%', display: 'grid', gap: 0.74, alignContent: 'start' }}>
              <Box>
                <Typography sx={{ ...darkOverlineSx, mb: 0.18 }}>What still has to hold</Typography>
                <Typography sx={{ color: 'white', fontWeight: 400, fontSize: '0.98rem', lineHeight: 1.32 }}>
                  Ormal optimizes the micro decision without losing the macro business conditions around it.
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gap: 0.55 }}>
                <Box sx={signalCellSx}>
                  <Typography sx={darkOverlineSx}>Macro conditions</Typography>
                  <Typography sx={{ mt: 0.18, color: 'white', fontWeight: 400, fontSize: '0.84rem', lineHeight: 1.46 }}>
                    Commitments, route economics, chargeback posture, and rollout reality still have to stay in line while the recommendation improves the payment moment.
                  </Typography>
                </Box>
                <Box sx={signalCellSx}>
                  <Typography sx={darkOverlineSx}>Returned decision shape</Typography>
                  <Typography sx={{ mt: 0.18, color: 'rgba(226,232,240,0.82)', fontSize: '0.8rem', lineHeight: 1.48 }}>
                    Recommendation, explanation, and expected business impact come back together so the team can review one answer instead of stitching several together.
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 0.55 }}>
                {[
                  ['Approval', 'Checkout conversion / approval lift'],
                  ['Economics', 'Better payment economics'],
                  ['Control', 'Tighter chargeback control'],
                ].map(([label, value]) => (
                  <Box key={label} sx={signalCellSx}>
                    <Typography sx={darkOverlineSx}>{label}</Typography>
                    <Typography sx={{ mt: 0.18, color: 'white', fontWeight: 400, fontSize: '0.88rem' }}>{value}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Grid>
        </Grid>

        {showAdaptationNote ? (
          <Box sx={{ ...lightBoardSx, p: 0.9 }}>
            <Typography sx={lightOverlineSx}>What the business gets back</Typography>
            <Typography sx={{ mt: 0.18, color: marketingPalette.body, fontSize: '0.82rem', lineHeight: 1.56 }}>
              The team gets one returned answer for payments: what to show, how to route it, why it makes sense, and what business impact to expect from the optimized stack.
            </Typography>
          </Box>
        ) : null}
      </Box>
    </Card>
  );
};

type ObjectiveControlBoardProps = {
  title?: string;
  caption?: string;
};

export const ObjectiveControlBoard: React.FC<ObjectiveControlBoardProps> = ({
  title = 'Every payment team is balancing conversion, cost, and risk.',
  caption = 'The team sets where to lean harder. Ormal makes the call while keeping the surrounding economics and risk in view.',
}) => {
  const priorities = [
    ['Lean into conversion', 'Push harder when more completed checkouts matter most.'],
    ['Lean into cost', 'Lean harder when fee drag and route economics matter more.'],
    ['Lean into risk', 'Pull tighter when dispute or liability pressure rises.'],
  ];

  return (
    <Card
      sx={{
        ...shellSx,
        p: { xs: 1.2, md: 1.35 },
        background: 'linear-gradient(180deg, rgba(255,255,255,0.995) 0%, rgba(248,251,255,0.995) 100%)',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '0.92fr 1.35fr' },
          gap: 1,
        }}
      >
        <Box
          sx={{
            ...darkBoardSx,
            p: 1.05,
            display: 'grid',
            alignContent: 'space-between',
            gap: 1.15,
          }}
        >
          <Box sx={{ display: 'grid', gap: 0.5 }}>
            <Typography sx={{ ...darkOverlineSx, color: 'rgba(147,197,253,0.85)' }}>Set what matters most</Typography>
            <Typography sx={{ color: 'white', fontWeight: 400, fontSize: '1rem', lineHeight: 1.36 }}>{title}</Typography>
            <Typography sx={{ color: 'rgba(226,232,240,0.82)', fontSize: '0.84rem', lineHeight: 1.55 }}>{caption}</Typography>
          </Box>
          <Box
            sx={{
              px: 0.92,
              py: 0.82,
              border: '1px solid rgba(120,143,180,0.2)',
              backgroundColor: 'rgba(255,255,255,0.03)',
            }}
          >
            <Typography sx={darkOverlineSx}>What stays in frame</Typography>
            <Typography sx={{ mt: 0.2, color: 'white', fontWeight: 400, fontSize: '0.82rem', lineHeight: 1.46 }}>
              Ormal can push harder on one goal without treating the other business constraints like they disappeared.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gap: 0.8 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 0.8 }}>
            {priorities.map(([label, body], index) => (
              <Box
                key={label}
                sx={{
                  borderTop: `3px solid ${index === 0 ? 'rgba(15,111,255,0.88)' : index === 1 ? 'rgba(14,165,233,0.88)' : 'rgba(16,38,77,0.88)'}`,
                  borderLeft: '1px solid rgba(214,223,234,0.9)',
                  borderRight: '1px solid rgba(214,223,234,0.9)',
                  borderBottom: '1px solid rgba(214,223,234,0.9)',
                  backgroundColor: '#fff',
                  px: 1.02,
                  py: 0.95,
                  display: 'grid',
                  gap: 0.52,
                }}
              >
                <Typography sx={{ ...lightOverlineSx, color: marketingPalette.accent }}>{label}</Typography>
                <Typography sx={{ color: marketingPalette.headline, fontWeight: 400, fontSize: '0.95rem', lineHeight: 1.52 }}>
                  {body}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              border: '1px solid rgba(214,223,234,0.9)',
              background: 'linear-gradient(180deg, rgba(248,251,255,0.98) 0%, rgba(240,247,255,0.98) 100%)',
              p: 1,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
              gap: 0.72,
            }}
          >
            {programFindings.map((item) => (
              <Box
                key={item.label}
                sx={{
                  borderLeft: '2px solid rgba(15,111,255,0.68)',
                  pl: 0.75,
                  pr: 0.2,
                  display: 'grid',
                  gap: 0.26,
                }}
              >
                <Typography sx={{ ...lightOverlineSx, color: marketingPalette.accent }}>{item.label}</Typography>
                <Typography sx={{ color: marketingPalette.headline, fontWeight: 400, fontSize: '0.8rem', lineHeight: 1.45 }}>
                  {item.detail}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

export const OptimizationResponseBoard: React.FC<{ initialScenarioId?: string }> = ({ initialScenarioId }) => {
  const { preset } = useScenarioPreset(initialScenarioId);

  return (
    <Card sx={shellSx}>
      <Box sx={{ display: 'grid', gap: 0.9 }}>
        <Grid container spacing={1} alignItems="stretch">
          <Grid item xs={12}>
            <Box sx={{ ...lightBoardSx, p: 1, minHeight: 434, height: '100%', display: 'grid', gap: 0.82 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <Box>
                  <Typography sx={lightOverlineSx}>What Ormal returns</Typography>
                  <Typography sx={{ mt: 0.16, color: marketingPalette.headline, fontWeight: 400, fontSize: '1rem', lineHeight: 1.3 }}>
                    One recommendation for the full payment moment.
                  </Typography>
                </Box>
                <Typography sx={{ color: marketingPalette.body, fontWeight: 400, fontSize: '0.8rem' }}>
                  Built to improve approval, economics, and control together
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.1fr 0.9fr 0.9fr' }, gap: 0.8 }}>
                <Box sx={{ px: 0.9, py: 0.86, border: '1px solid rgba(219,228,239,0.88)', backgroundColor: '#fff', display: 'grid', gap: 0.58 }}>
                  <Typography sx={lightOverlineSx}>Recommendation</Typography>
                  <Box sx={{ display: 'grid', gap: 0.48 }}>
                    <Box>
                      <Typography sx={{ color: marketingPalette.headline, fontWeight: 400, fontSize: '0.84rem', lineHeight: 1.4 }}>
                        Visible checkout
                      </Typography>
                      <Typography sx={{ mt: 0.14, color: marketingPalette.body, fontSize: '0.8rem', lineHeight: 1.52 }}>
                        Method order, lead method, and fallback presentation move as one recommendation.
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ color: marketingPalette.headline, fontWeight: 400, fontSize: '0.84rem', lineHeight: 1.4 }}>
                        Authorization posture
                      </Typography>
                      <Typography sx={{ mt: 0.14, color: marketingPalette.body, fontSize: '0.8rem', lineHeight: 1.52 }}>
                        The hidden route can change underneath the same visible checkout when that improves the business answer.
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ px: 0.9, py: 0.86, border: '1px solid rgba(219,228,239,0.88)', backgroundColor: '#fff', display: 'grid', gap: 0.58 }}>
                  <Typography sx={lightOverlineSx}>How Ormal decides</Typography>
                  <Box>
                    <Typography sx={{ color: marketingPalette.headline, fontWeight: 400, fontSize: '0.84rem', lineHeight: 1.42 }}>
                      What it considers
                    </Typography>
                    <Typography sx={{ mt: 0.14, color: marketingPalette.body, fontSize: '0.8rem', lineHeight: 1.52 }}>
                      Buyer/payment context, route quality, payment economics, chargeback pressure, and what the business wants to push harder on.
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ color: marketingPalette.headline, fontWeight: 400, fontSize: '0.84rem', lineHeight: 1.42 }}>
                      Macro conditions it keeps in line
                    </Typography>
                    <Typography sx={{ mt: 0.14, color: marketingPalette.body, fontSize: '0.8rem', lineHeight: 1.52 }}>
                      Economics, thresholds, commitments, and risk posture stay in frame instead of being checked after the fact.
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ ...darkBoardSx, p: 0.9, display: 'grid', gap: 0.55 }}>
                  <Typography sx={darkOverlineSx}>Expected impact from the optimized stack</Typography>
                  {[
                    ['Approval', 'Stronger checkout conversion / approval'],
                    ['Economics', 'Stronger payment economics'],
                    ['Control', 'Tighter chargeback and risk control'],
                  ].map(([label, value]) => (
                    <Box key={label} sx={impactCellSx}>
                      <Typography sx={darkOverlineSx}>{label}</Typography>
                      <Typography sx={{ mt: 0.18, color: 'white', fontWeight: 400, fontSize: '0.92rem' }}>{value}</Typography>
                    </Box>
                  ))}
                  <Box sx={{ px: 0.86, py: 0.74, border: '1px solid rgba(120,143,180,0.2)' }}>
                    <Typography sx={darkOverlineSx}>Why the answer holds</Typography>
                    <Typography sx={{ mt: 0.18, color: 'rgba(226,232,240,0.84)', fontSize: '0.78rem', lineHeight: 1.46 }}>
                      The returned answer is meant to improve the local payment decision without quietly giving the win back elsewhere in the stack.
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ border: '1px solid rgba(219,228,239,0.88)', px: 0.9, py: 0.82, backgroundColor: '#f8fbff' }}>
                <Typography sx={lightOverlineSx}>How the answer is packaged</Typography>
                <Typography sx={{ color: marketingPalette.body, fontSize: '0.8rem', lineHeight: 1.55, mb: 0.42 }}>
                  The recommendation is delivered as one decision object the team can review, ship, and defend across product, payments, and engineering.
                </Typography>
                <Typography sx={{ ...monoSx, color: marketingPalette.headline }}>{preset.responsePayload.split('\n').slice(0, 4).join('\n')}</Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Card>
  );
};

export const ProgramInsightBoard: React.FC<{
  title?: string;
  summary?: string;
}> = ({
  title = 'Why the result holds up',
  summary = 'Ormal does not just improve the checkout result. It also reads the surrounding economics so a short-term win does not come back as worse fees, broken commitments, or weaker risk control.',
}) => {
  return (
    <Card
      sx={{
        ...shellSx,
        p: 1.08,
        height: '100%',
        background: 'linear-gradient(180deg, rgba(250,252,255,0.995) 0%, rgba(243,248,255,0.995) 100%)',
      }}
    >
      <Typography sx={{ color: marketingPalette.headline, fontWeight: 400, fontSize: '1rem', lineHeight: 1.34 }}>{title}</Typography>
      <Typography sx={{ mt: 0.4, color: marketingPalette.body, fontSize: '0.84rem', lineHeight: 1.56 }}>{summary}</Typography>
      <Box sx={{ mt: 0.9, display: 'grid', gap: 0.72 }}>
        {programFindings.map((item) => (
          <Box key={item.label} sx={{ pb: 0.72, borderBottom: '1px solid rgba(214,223,234,0.9)' }}>
            <Typography sx={{ ...lightOverlineSx, color: marketingPalette.accent }}>{item.label}</Typography>
            <Typography sx={{ mt: 0.18, color: marketingPalette.headline, fontWeight: 400, fontSize: '0.84rem', lineHeight: 1.48 }}>
              {item.detail}
            </Typography>
          </Box>
        ))}
      </Box>
    </Card>
  );
};

export const ProofPerformanceBoard: React.FC = () => {
  return (
    <Card
      sx={{
        ...shellSx,
        p: 1.08,
        height: '100%',
        background: 'linear-gradient(180deg, rgba(255,255,255,0.995) 0%, rgba(246,250,255,0.995) 100%)',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, mb: 0.9 }}>
        <Typography sx={{ color: marketingPalette.headline, fontWeight: 400, fontSize: '1rem' }}>Pilot results</Typography>
        <Typography sx={{ ...lightOverlineSx, color: marketingPalette.accent }}>Baseline / with Ormal / still healthy</Typography>
      </Box>
      <Box sx={{ display: 'grid', gap: 0.55 }}>
        {proofPilots.map((row) => (
          <Box
            key={row.label}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1.1fr 1fr 1fr 1fr' },
              gap: 0.55,
              px: 0.78,
              py: 0.72,
              border: '1px solid rgba(214,223,234,0.9)',
              backgroundColor: '#fff',
            }}
          >
            <Box>
              <Typography sx={lightOverlineSx}>{row.label}</Typography>
              <Typography sx={{ mt: 0.16, color: marketingPalette.headline, fontWeight: 400, fontSize: '0.84rem', lineHeight: 1.4 }}>{row.headline}</Typography>
              <Typography sx={{ mt: 0.16, color: marketingPalette.body, fontWeight: 400, fontSize: '0.8rem', lineHeight: 1.4 }}>{row.company}</Typography>
            </Box>
            <Box>
              <Typography sx={lightOverlineSx}>Baseline</Typography>
              <Typography sx={{ mt: 0.16, color: marketingPalette.body, fontWeight: 400, fontSize: '0.84rem', lineHeight: 1.42 }}>{row.baseline}</Typography>
            </Box>
            <Box>
              <Typography sx={lightOverlineSx}>With Ormal</Typography>
              <Typography sx={{ mt: 0.16, color: marketingPalette.accent, fontWeight: 400, fontSize: '0.84rem', lineHeight: 1.42 }}>{row.optimized}</Typography>
            </Box>
            <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' } }}>
              <Typography sx={lightOverlineSx}>Still healthy</Typography>
              <Typography sx={{ mt: 0.16, color: marketingPalette.headline, fontWeight: 400, fontSize: '0.84rem', lineHeight: 1.42 }}>{row.protected}</Typography>
            </Box>
          </Box>
        ))}
      </Box>
      <Box sx={{ mt: 0.9, px: 0.9, py: 0.84, border: '1px solid rgba(15,111,255,0.18)', backgroundColor: 'rgba(15,111,255,0.06)' }}>
        <Typography sx={{ ...lightOverlineSx, color: marketingPalette.accent }}>Executive takeaway</Typography>
        <Typography sx={{ mt: 0.18, color: marketingPalette.headline, fontWeight: 400, fontSize: '0.84rem', lineHeight: 1.54 }}>
          Across two pilots, Ormal improved checkout performance and payment economics without giving back risk control or coverage.
        </Typography>
      </Box>
    </Card>
  );
};

export const ProductJourneyArtifact: React.FC = () => <ScenarioCheckoutHero />;
export const PriorityTriangle: React.FC<ObjectiveControlBoardProps> = (props) => <ObjectiveControlBoard {...props} />;
export const ProofDeltaVisual: React.FC = () => <ProofPerformanceBoard />;
export const RequestResponseArtifact: React.FC<{ initialScenarioId?: string }> = (props) => <OptimizationResponseBoard {...props} />;
export const SolutionOptimizationArtifact: React.FC = () => <SolutionScenarioBoard initialScenarioId="cross-border" showAdaptationNote />;
